import { CountryCode, EnergyClass } from '@ev-tech/search-modules-api';

export const Color = {
  DarkestGreen: '#178100',
  DarkerGreen: '#629B1D',
  Green: '#73A931',
  LightGreen: '#96B131',
  OldGold: '#D1CC35',
  Yellow: '#F4C832',
  LightestOrange: '#F0A221',
  LighterOrange: '#EE922E',
  Orange: '#EF7910',
  DarkOrange: '#D84B09',
  Salmon: '#DE5637',
  Red: '#B30100',
  Purple: '#4A224F',
  Blue: '#4394D0',
} as const;
export type Color = (typeof Color)[keyof typeof Color];

// Info: Right now we only have energy class info for DE and BE. The rest of the countries are listed here as preparation for when we have data for those countries
export const getCountryColorMap = (countryCode: CountryCode): [EnergyClass, Color][] => {
  switch (countryCode) {
    case CountryCode.AD:
    case CountryCode.CH:
    case CountryCode.ES:
    case CountryCode.FR:
    case CountryCode.CZ:
      return [
        [EnergyClass.A, Color.DarkestGreen],
        [EnergyClass.B, Color.Green],
        [EnergyClass.C, Color.OldGold],
        [EnergyClass.D, Color.Yellow],
        [EnergyClass.E, Color.LightestOrange],
        [EnergyClass.F, Color.Orange],
        [EnergyClass.G, Color.DarkOrange],
      ];
    case CountryCode.NL:
      return [
        [EnergyClass['A_4_PLUS'], Color.DarkestGreen],
        [EnergyClass['A_3_PLUS'], Color.DarkestGreen],
        [EnergyClass['A_2_PLUS'], Color.DarkestGreen],
        [EnergyClass['A_PLUS'], Color.DarkestGreen],
        [EnergyClass.A, Color.DarkestGreen],
        [EnergyClass.B, Color.Green],
        [EnergyClass.C, Color.OldGold],
        [EnergyClass.D, Color.Yellow],
        [EnergyClass.E, Color.LightestOrange],
        [EnergyClass.F, Color.Orange],
        [EnergyClass.G, Color.DarkOrange],
      ];
    case CountryCode.DE:
      return [
        [EnergyClass['A_PLUS'], Color.DarkestGreen],
        [EnergyClass.A, Color.Green],
        [EnergyClass.B, Color.LightGreen],
        [EnergyClass.C, Color.OldGold],
        [EnergyClass.D, Color.Yellow],
        [EnergyClass.E, Color.LightestOrange],
        [EnergyClass.F, Color.Orange],
        [EnergyClass.G, Color.DarkOrange],
        [EnergyClass.H, Color.Red],
      ];
    case CountryCode.AT:
      return [
        [EnergyClass['A_2_PLUS'], Color.DarkestGreen],
        [EnergyClass['A_PLUS'], Color.Green],
        [EnergyClass.A, Color.LightGreen],
        [EnergyClass.B, Color.OldGold],
        [EnergyClass.C, Color.Yellow],
        [EnergyClass.D, Color.LightestOrange],
        [EnergyClass.E, Color.Orange],
        [EnergyClass.F, Color.DarkOrange],
        [EnergyClass.G, Color.Red],
      ];
    case CountryCode.BE:
    case CountryCode.HR:
      return [
        [EnergyClass['A_PLUS'], Color.DarkestGreen],
        [EnergyClass.A, Color.Green],
        [EnergyClass.B, Color.LightGreen],
        [EnergyClass.C, Color.Yellow],
        [EnergyClass.D, Color.LightestOrange],
        [EnergyClass.E, Color.Orange],
        [EnergyClass.F, Color.DarkOrange],
        [EnergyClass.G, Color.Red],
      ];
    case CountryCode.IE:
      return [
        [EnergyClass.A0, Color.Blue],
        [EnergyClass.A, Color.DarkestGreen],
        [EnergyClass.B, Color.Green],
        [EnergyClass.C, Color.LightGreen],
        [EnergyClass.D, Color.Yellow],
        [EnergyClass.E, Color.LightestOrange],
        [EnergyClass.F, Color.DarkOrange],
        [EnergyClass.G, Color.Red],
      ];
    case CountryCode.HU:
      return [
        [EnergyClass['A_3_PLUS'], Color.DarkestGreen],
        [EnergyClass['A_2_PLUS'], Color.Green],
        [EnergyClass['A_PLUS'], Color.Green],
        [EnergyClass.A, Color.Green],
        [EnergyClass.B, Color.LightGreen],
        [EnergyClass.C, Color.OldGold],
        [EnergyClass.D, Color.Yellow],
        [EnergyClass.E, Color.LightestOrange],
        [EnergyClass.F, Color.Orange],
        [EnergyClass.G, Color.DarkOrange],
        [EnergyClass.H, Color.Red],
        [EnergyClass.I, Color.Purple],
      ];
    case CountryCode.IT:
      return [
        [EnergyClass.A4, Color.DarkestGreen],
        [EnergyClass.A3, Color.DarkerGreen],
        [EnergyClass.A2, Color.Green],
        [EnergyClass.A1, Color.LightGreen],
        [EnergyClass.B, Color.OldGold],
        [EnergyClass.C, Color.Yellow],
        [EnergyClass.D, Color.LightestOrange],
        [EnergyClass.E, Color.Orange],
        [EnergyClass.F, Color.DarkOrange],
        [EnergyClass.G, Color.Red],
      ];
    case CountryCode.GR:
      return [
        [EnergyClass['A_PLUS'], Color.DarkestGreen],
        [EnergyClass.A, Color.DarkerGreen],
        [EnergyClass['B_PLUS'], Color.Green],
        [EnergyClass.B, Color.LightGreen],
        [EnergyClass.C, Color.Yellow],
        [EnergyClass.D, Color.LightestOrange],
        [EnergyClass.E, Color.Orange],
        [EnergyClass.F, Color.DarkOrange],
        [EnergyClass.G, Color.Red],
      ];
    case CountryCode.PT:
      return [
        [EnergyClass['A_PLUS'], Color.DarkestGreen],
        [EnergyClass.A, Color.DarkerGreen],
        [EnergyClass.B, Color.Green],
        [EnergyClass['B_MINUS'], Color.LightGreen],
        [EnergyClass.C, Color.OldGold],
        [EnergyClass.D, Color.Yellow],
        [EnergyClass.E, Color.LightestOrange],
        [EnergyClass.F, Color.Red],
      ];
    case CountryCode.LU:
      return [
        [EnergyClass.A, Color.DarkestGreen],
        [EnergyClass.B, Color.Green],
        [EnergyClass.C, Color.LightGreen],
        [EnergyClass.D, Color.OldGold],
        [EnergyClass.E, Color.Yellow],
        [EnergyClass.F, Color.LightestOrange],
        [EnergyClass.G, Color.Orange],
        [EnergyClass.H, Color.DarkOrange],
        [EnergyClass.I, Color.Red],
      ];
    case CountryCode.DK:
      return [
        [EnergyClass.A20, Color.DarkestGreen],
        [EnergyClass.A15, Color.Green],
        [EnergyClass.A10, Color.LightGreen],
        [EnergyClass.B, Color.OldGold],
        [EnergyClass.C, Color.Yellow],
        [EnergyClass.D, Color.LighterOrange],
        [EnergyClass.E, Color.Salmon],
        [EnergyClass.F, Color.Red],
        [EnergyClass.G, Color.Purple],
      ];
    default:
      return [
        [EnergyClass['A_PLUS'], Color.DarkestGreen],
        [EnergyClass.A, Color.Green],
        [EnergyClass.B, Color.LightGreen],
        [EnergyClass.C, Color.OldGold],
        [EnergyClass.D, Color.Yellow],
        [EnergyClass.E, Color.LightestOrange],
        [EnergyClass.F, Color.Orange],
        [EnergyClass.G, Color.DarkOrange],
        [EnergyClass.H, Color.Red],
      ];
  }
};

export const EnergyClassLabelMap: Record<EnergyClass, string> = {
  [EnergyClass.UNKNOWN]: 'UNKNOWN',
  [EnergyClass.NOT_APPLICABLE]: 'NOT APPLICABLE',
  [EnergyClass.IN_PROGRESS]: 'IN PROGRESS',
  [EnergyClass.A_PLUS]: 'A+',
  [EnergyClass.A]: 'A',
  [EnergyClass.B]: 'B',
  [EnergyClass.C]: 'C',
  [EnergyClass.D]: 'D',
  [EnergyClass.E]: 'E',
  [EnergyClass.F]: 'F',
  [EnergyClass.G]: 'G',
  [EnergyClass.H]: 'H',
  [EnergyClass.I]: 'I',
  [EnergyClass.A20]: 'A20',
  [EnergyClass.A15]: 'A15',
  [EnergyClass.A10]: 'A10',
  [EnergyClass.B1]: 'B1',
  [EnergyClass.B2]: 'B2',
  [EnergyClass.B3]: 'B3',
  [EnergyClass.C1]: 'C1',
  [EnergyClass.C2]: 'C2',
  [EnergyClass.C3]: 'C3',
  [EnergyClass.D1]: 'D1',
  [EnergyClass.D2]: 'D2',
  [EnergyClass.E1]: 'E1',
  [EnergyClass.E2]: 'E2',
  [EnergyClass.B_PLUS]: 'B+',
  [EnergyClass.B_MINUS]: 'B-',
  [EnergyClass.A_2010]: 'A2010',
  [EnergyClass.A_2015]: 'A2015',
  [EnergyClass.A_2020]: 'A2020',
  [EnergyClass.A_2_PLUS]: 'A++',
  [EnergyClass.A_3_PLUS]: 'A+++',
  [EnergyClass.A_4_PLUS]: 'A++++',
  [EnergyClass.A4]: 'A4',
  [EnergyClass.A3]: 'A3',
  [EnergyClass.A2]: 'A2',
  [EnergyClass.A1]: 'A1',
  [EnergyClass.A0]: 'A0',
} as const;
